import React from "react";
import { useNavigate } from "react-router-dom";
import { HiGift, HiStar, HiShoppingCart, HiSparkles } from "react-icons/hi";
import { useTheme } from "../../hooks/ThemeContext";
import { useRestaurant } from "../../hooks/RestaurantContext";
import { useDineCentralAuth } from "../../hooks/useDineCentralAuth";
import { useConfig } from "../../hooks/ConfigProvider";
import { getRemoteEnv } from "../../config/remoteEnv";
import ToastService from "../../service/ToastService";
import {
  fetchLoyaltyPublicConfig,
  fetchMyLoyalty,
  cartRewardPoints,
  redemptionOf,
} from "../../service/LoyaltyService";
import type { LoyaltyRedemption, LoyaltyReward } from "../../service/LoyaltyService";
import RedeemModal from "./RedeemModal";
import QrScanner, { parseTillLink } from "./QrScanner";

// Rewards tab — the customer's points balance and the reward menu as a card
// grid, cheapest reward first. ONE stacked structure at every width — the
// mobile layout, centered in a 680px column (no desktop two-column variant).
// Rewards the balance can't cover stay FULL COLOUR (greyed cards made the
// menu look worse) — tapping one shows a "N more points" toast instead.
// Tapping an affordable reward opens RedeemModal with the ways this store
// allows (client center → Reward Items): "Add to cart" puts the REAL menu
// item in the cart as a points-paid line (item.loyaltyReward) — only while
// online ordering is on; the points are spent at payment time, after PPB
// re-validates everything server-side. "Redeem in store" only explains what
// to do at the till (scan its QR in-site, or give staff their email).

type Props = {
  cart: any[];
  setCart: any;
  productData: any[];
  testMode: boolean;
};

const css = `
/* width:100% — this page is a flex item of the app's page-container; with
   width auto it shrink-wraps to content, which pins the shell to the LEFT
   (margin:auto has no room) and leaves the card grid without a definite
   width, collapsing auto-fill to one oversized column. */
.rw-page { width: 100%; min-height: 100%; overflow-y: auto; box-sizing: border-box; -webkit-font-smoothing: antialiased; text-align: left; }
/* One centred column — the same stacked structure at every width. */
.rw-shell { max-width: 680px; margin: 0 auto; padding: 26px 16px 110px; box-sizing: border-box; }
.rw-head { margin-bottom: 20px; }
.rw-title { font-size: 26px; font-weight: 800; letter-spacing: -0.4px; display: flex; align-items: center; gap: 10px; margin: 0 0 4px; }
.rw-title svg { color: var(--rw-accent); }
.rw-sub { opacity: .62; font-size: 14px; margin: 0; line-height: 1.5; }

.rw-grid { display: grid; grid-template-columns: 1fr; gap: 22px; align-items: start; }

/* ── Balance / sign-in rail ─────────────────────────────────────── */
.rw-balance {
  border-radius: 20px; padding: 24px; color: #fff; position: relative; overflow: hidden;
  background: linear-gradient(135deg, var(--rw-accent) 0%, var(--rw-accent-deep) 100%);
  box-shadow: 0 10px 30px var(--rw-accent-glow);
}
.rw-balance-star { position: absolute; right: -18px; top: -18px; font-size: 110px; opacity: .12; transform: rotate(18deg); }
.rw-balance-label { font-size: 11px; font-weight: 700; letter-spacing: .12em; opacity: .85; margin-bottom: 8px; }
.rw-balance-num { font-size: 46px; font-weight: 800; line-height: 1; letter-spacing: -1px; }
.rw-balance-num small { font-size: 16px; font-weight: 600; opacity: .8; letter-spacing: 0; margin-left: 4px; }
.rw-balance-cart { font-size: 12.5px; font-weight: 600; opacity: .85; margin-top: 7px; }
.rw-next { margin-top: 18px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,.25); }
.rw-next-label { font-size: 12.5px; font-weight: 600; opacity: .9; margin-bottom: 8px; display: flex; justify-content: space-between; gap: 8px; }
.rw-bar { height: 7px; border-radius: 99px; background: rgba(255,255,255,.25); overflow: hidden; }
.rw-bar > i { display: block; height: 100%; border-radius: 99px; background: #fff; transition: width .6s cubic-bezier(.2,.7,.2,1); }

.rw-balance-row { position: relative; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.rw-lifetime { text-align: right; margin-left: auto; }
.rw-lifetime .rw-balance-label { margin-bottom: 8px; }
.rw-lifetime-sub { font-size: 11.5px; font-weight: 600; opacity: .75; margin-top: 6px; max-width: 150px; line-height: 1.4; margin-left: auto; }
@keyframes rw-pulse { 0%, 100% { opacity: .45; } 50% { opacity: .9; } }
.rw-loading-num { animation: rw-pulse 1.2s ease-in-out infinite; }

.rw-signin {
  border-radius: 20px; padding: 24px; color: #fff; position: relative; overflow: hidden;
  background: linear-gradient(135deg, var(--rw-accent) 0%, var(--rw-accent-deep) 100%);
  box-shadow: 0 10px 30px var(--rw-accent-glow);
}
.rw-signin-title { font-weight: 800; font-size: 19px; margin-bottom: 5px; letter-spacing: -0.2px; }
.rw-signin-sub { font-size: 13.5px; opacity: .88; line-height: 1.55; margin-bottom: 16px; }
.rw-btn.invert { background: #fff; color: var(--rw-accent); }
.rw-btn.invert:hover { box-shadow: 0 6px 16px rgba(0,0,0,.25); }
.rw-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  background: var(--rw-accent); color: #fff; border: none; border-radius: 11px;
  padding: 12px 20px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit;
  transition: transform .12s ease, box-shadow .12s ease;
}
.rw-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px var(--rw-accent-glow); }
.rw-btn:active { transform: none; }

.rw-how { margin-top: 18px; border-radius: 18px; padding: 20px 22px; background: var(--rw-card); border: 1px solid var(--rw-border); }
.rw-how-title { font-size: 11px; font-weight: 700; letter-spacing: .1em; opacity: .55; margin-bottom: 14px; }
.rw-how-row { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 13px; }
.rw-how-row:last-child { margin-bottom: 0; }
.rw-how-dot {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  background: var(--rw-accent-soft); color: var(--rw-accent); font-size: 12.5px; font-weight: 800;
}
.rw-how-row b { display: block; font-size: 13.5px; margin-bottom: 1px; }
.rw-how-row span { font-size: 12.5px; opacity: .6; line-height: 1.45; }

/* ── Reward card grid ───────────────────────────────────────────── */
.rw-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(215px, 1fr)); gap: 14px; }
.rw-card {
  background: var(--rw-card); border: 1px solid var(--rw-border); border-radius: 18px;
  overflow: hidden; display: flex; flex-direction: column;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.rw-card.on { border-color: var(--rw-accent-border); }
.rw-card.on:hover, .rw-card.locked:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,.10); }
/* Unaffordable cards stay full colour — tapping them explains via toast. */
.rw-card.locked { cursor: pointer; }
.rw-figure {
  position: relative; width: 100%; aspect-ratio: 16 / 10; background: var(--rw-accent-soft);
  display: flex; align-items: center; justify-content: center;
}
.rw-figure img { position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: cover; }
.rw-gift { font-size: 36px; color: var(--rw-accent); }
.rw-body { padding: 13px 14px 14px; display: flex; flex-direction: column; gap: 10px; flex: 1; }
.rw-name { font-weight: 700; font-size: 15px; line-height: 1.3; }
.rw-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: auto; flex-wrap: wrap; }
.rw-pill {
  display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 800;
  color: var(--rw-accent); background: var(--rw-accent-soft); border: 1px solid var(--rw-accent-border);
  padding: 3px 10px; border-radius: 999px; white-space: nowrap;
}
.rw-more { font-size: 12px; opacity: .55; font-weight: 600; white-space: nowrap; }
.rw-note { font-size: 12px; opacity: .55; font-weight: 600; }
.rw-redeem { width: 100%; padding: 11px 14px; font-size: 13.5px; border-radius: 11px; }

@media (max-width: 520px) {
  .rw-title { font-size: 23px; }
  .rw-balance { padding: 20px; border-radius: 18px; }
  .rw-balance-num { font-size: 38px; }
  .rw-lifetime-sub { display: none; }
  .rw-cards { grid-template-columns: repeat(auto-fill, minmax(152px, 1fr)); gap: 11px; }
  .rw-card { border-radius: 15px; }
  .rw-body { padding: 11px 12px 12px; gap: 8px; }
  .rw-name { font-size: 13.5px; }
  .rw-redeem { padding: 10px 12px; font-size: 12.5px; }
  .rw-redeem .rw-redeem-icon { display: none; }
  .rw-how { display: none; }
}

.rw-state { padding: 40px 12px; text-align: center; opacity: .6; font-size: 14.5px; }
.rw-error { border-radius: 14px; padding: 14px 16px; background: rgba(229,72,77,.08); border: 1px solid rgba(229,72,77,.25); color: #e5484d; font-size: 13.5px; margin-bottom: 18px; }
`;

// The accent needs derived tones (deep gradient stop, soft fill, glow). All
// alpha-based so they behave on any brand accent and both themes.
const hexToRgb = (hex: string): [number, number, number] | null => {
  const m = hex.replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
};

const RewardsPage = (props: Props) => {
  const { theme } = useTheme();
  const { restaurantId } = useRestaurant();
  const { config } = useConfig();
  const auth = useDineCentralAuth();
  const navigate = useNavigate();

  const [rewards, setRewards] = React.useState<LoyaltyReward[] | null>(null);
  const [enabled, setEnabled] = React.useState<boolean | null>(null);
  const [balance, setBalance] = React.useState<number | null>(null); // visible pts
  const [lifetime, setLifetime] = React.useState<number | null>(null);
  const [redemption, setRedemption] = React.useState<LoyaltyRedemption>(redemptionOf(null));
  const [error, setError] = React.useState("");
  const [redeeming, setRedeeming] = React.useState<null | { reward: LoyaltyReward; product: any }>(null);
  const [scanFor, setScanFor] = React.useState<null | string>(null); // reward name being redeemed in store

  const signedIn = !!auth.user && !auth.loading;

  React.useEffect(() => {
    if (!restaurantId) return;
    let live = true;
    setError("");
    (signedIn
      ? fetchMyLoyalty(restaurantId, props.testMode).then((s) => {
          if (!live) return;
          setEnabled(s.enabled);
          setRewards(s.rewards);
          setBalance(s.visiblePoints);
          setLifetime(typeof s.lifetimePoints === "number" ? s.lifetimePoints : null);
          setRedemption(redemptionOf(s));
        })
      : fetchLoyaltyPublicConfig(restaurantId).then((c) => {
          if (!live) return;
          setEnabled(c.enabled);
          setRewards(c.rewards);
          setBalance(null);
          setLifetime(null);
          setRedemption(redemptionOf(c));
        })
    ).catch((e: any) => live && setError(e?.message || "Couldn't load rewards."));
    return () => {
      live = false;
    };
  }, [restaurantId, signedIn, props.testMode]);

  const accent = getRemoteEnv().themeOverrideAccentColor || theme.payButtonColor || "#4169E1";
  const rgb = hexToRgb(accent) || [65, 105, 225];
  const accentDeep = `rgb(${Math.round(rgb[0] * 0.62)}, ${Math.round(rgb[1] * 0.62)}, ${Math.round(rgb[2] * 0.62)})`;
  const accentSoft = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.12)`;
  const accentBorder = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.35)`;
  const accentGlow = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.30)`;

  // Same "is this store taking online orders" rule as OrderPage / ProductCard.
  const restaurant = config?.restaurants?.[restaurantId];
  const orderingOn =
    (!getRemoteEnv().menuOnly && restaurant?.general?.onlineOrderingEnabled === true) ||
    window.location.hostname.includes("demo.");
  const canOnline = redemption.online && orderingOn;
  const canRedeem = canOnline || redemption.inStore;

  const pointsInCart = cartRewardPoints(props.cart);
  const available = balance !== null ? balance - pointsInCart : null;

  const sorted = (rewards || []).slice().sort((a, b) => a.points - b.points);
  const affordableCount = available !== null ? sorted.filter((r) => r.points <= available).length : 0;
  const nextReward = available !== null ? sorted.find((r) => r.points > available) : undefined;
  const nextPct = nextReward && available !== null ? Math.max(4, Math.min(100, (available / nextReward.points) * 100)) : 100;

  const addReward = (reward: LoyaltyReward, product: any) => {
    props.setCart((prev: any[]) => [
      ...prev,
      {
        id: product.productId,
        selectedChoices: [],
        additionalOptionIds: [],
        additionalOptionsMetadata: [],
        additionalRequests: "",
        taxExemptOnTakeout: true,
        takeoutTaxableAmount: 0,
        taxableAmount: 0,
        quantity: 1,
        menus: product.menus || [],
        // Paid in points — validated server-side by PPB before any money
        // moves; the points figure here is display-only.
        loyaltyReward: { rewardId: reward.id, points: reward.points },
      },
    ]);
    ToastService.showToast(`${product.productName} added to cart — ${reward.points} points at checkout`, {
      style: { backgroundColor: "darkgreen", color: "white", minHeight: "80px", borderRadius: "15px" },
    });
  };

  // A scanned till QR opens this site's /link page (same brand) with the
  // reward named, so the confirmation can remind them to ask for it.
  const onScanned = (text: string): boolean => {
    const link = parseTillLink(text);
    if (!link) return false;
    const reward = scanFor || "";
    setScanFor(null);
    if (link.appId === getRemoteEnv().appId) {
      navigate(`/link${link.search}${reward ? `&reward=${encodeURIComponent(reward)}` : ""}`);
    } else {
      window.location.assign(link.url);
    }
    return true;
  };

  const vars = {
    "--rw-accent": accent,
    "--rw-accent-deep": accentDeep,
    "--rw-accent-soft": accentSoft,
    "--rw-accent-border": accentBorder,
    "--rw-accent-glow": accentGlow,
    "--rw-card": theme.cardColor,
    "--rw-border": theme.borderColor,
    "--rw-page-bg": theme.backgroundColor,
    "--rw-faint": theme.borderColor,
  } as React.CSSProperties;

  return (
    <div className="rw-page" style={{ backgroundColor: theme.backgroundColor, color: theme.textColor, ...vars }}>
      <style>{css}</style>
      <div className="rw-shell">
        <div className="rw-head">
          <h1 className="rw-title">
            <HiGift /> Rewards
          </h1>
          <p className="rw-sub">Earn points every time you order — spend them on the good stuff.</p>
        </div>

        {error && <div className="rw-error">{error}</div>}

        <div className="rw-grid">
          {/* ── Left rail: balance / lifetime / sign-in / how it works ── */}
          <div className="rw-side">
            {/* Never pitch "Sign in" at someone who IS signed in: while auth
                or the balance fetch is still resolving, show the balance
                card in a loading state instead. */}
            {auth.loading || (signedIn && balance === null && !error) ? (
              <div className="rw-balance">
                <HiStar className="rw-balance-star" />
                <div className="rw-balance-label">YOUR POINTS</div>
                <div className="rw-balance-num rw-loading-num">
                  …<small>pts</small>
                </div>
              </div>
            ) : signedIn && balance !== null ? (
              <>
                <div className="rw-balance">
                  <HiStar className="rw-balance-star" />
                  <div className="rw-balance-row">
                    <div>
                      <div className="rw-balance-label">YOUR POINTS</div>
                      <div className="rw-balance-num">
                        {balance.toLocaleString()}
                        <small>pts</small>
                      </div>
                    </div>
                    {lifetime !== null && (
                      <div className="rw-lifetime">
                        <div className="rw-balance-label">LIFETIME EARNED</div>
                        <div className="rw-balance-num">
                          {lifetime.toLocaleString()}
                          <small>pts</small>
                        </div>
                        <div className="rw-lifetime-sub">Every order pushes it higher</div>
                      </div>
                    )}
                  </div>
                  {pointsInCart > 0 && <div className="rw-balance-cart">− {pointsInCart.toLocaleString()} reserved in your cart</div>}
                  {nextReward ? (
                    <div className="rw-next">
                      <div className="rw-next-label">
                        <span>Next: {nextReward.name}</span>
                        <span>
                          {Math.max(0, nextReward.points - (available ?? 0)).toLocaleString()} to go
                        </span>
                      </div>
                      <div className="rw-bar">
                        <i style={{ width: `${nextPct}%` }} />
                      </div>
                    </div>
                  ) : sorted.length > 0 && affordableCount === sorted.length ? (
                    <div className="rw-next">
                      <div className="rw-next-label">
                        <span>
                          <HiSparkles style={{ verticalAlign: "-2px" }} /> Every reward is within reach!
                        </span>
                      </div>
                      <div className="rw-bar">
                        <i style={{ width: "100%" }} />
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : !auth.loading ? (
              <div className="rw-signin">
                <div className="rw-signin-title">Sign in to see your points</div>
                <div className="rw-signin-sub">Every order earns points on your account — and they add up fast.</div>
                <button className="rw-btn invert" onClick={() => navigate(`/login?redirect=${encodeURIComponent("/rewards")}`)}>
                  Sign in
                </button>
              </div>
            ) : null}

            <div className="rw-how">
              <div className="rw-how-title">HOW IT WORKS</div>
              <div className="rw-how-row">
                <span className="rw-how-dot">1</span>
                <div>
                  <b>Order as usual</b>
                  <span>Points land on your account with every paid order.</span>
                </div>
              </div>
              <div className="rw-how-row">
                <span className="rw-how-dot">2</span>
                <div>
                  <b>Watch them stack up</b>
                  <span>Your balance carries across visits — no expiry rush.</span>
                </div>
              </div>
              <div className="rw-how-row">
                <span className="rw-how-dot">3</span>
                <div>
                  <b>Treat yourself</b>
                  <span>
                    {canOnline && redemption.inStore
                      ? "Tap a reward to add it to your order, or claim it at the till."
                      : canOnline
                        ? "Add a reward to your cart — the points settle at checkout."
                        : "Tap a reward, then claim it at the till on your next visit."}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: reward card grid ── */}
          <div>
            {enabled === false && <div className="rw-state">Rewards aren't available here right now.</div>}
            {rewards === null && !error && <div className="rw-state">Loading rewards…</div>}
            {rewards !== null && enabled !== false && sorted.length === 0 && (
              <div className="rw-state">The reward menu is being cooked up — check back soon!</div>
            )}

            {sorted.length > 0 && (
              <div className="rw-cards">
                {sorted.map((r) => {
                  // By id; else by NAME — a menu re-import gives items new
                  // ids and a reward saved before it points at nothing (Hot
                  // Birdies, 2026-09-23: every reward lost its photo and
                  // became unredeemable). Checkout validates the reward id,
                  // never the product id, so the current item is safe to use.
                  const norm = (x: any) => String(x || "").trim().toLowerCase();
                  const found =
                    props.productData.find((p: any) => p.productId === r.productId) ||
                    props.productData.find((p: any) => p.productType !== "additional" && !p.isDeleted && norm(p.productName) === norm(r.name));
                  // A reward is only offerable while its menu item actually
                  // is: sold-out / disabled / deleted items show as
                  // unavailable instead of failing later at checkout.
                  const orderable = !!found && !found.isDeleted && !found.isDisabled && !found.outOfStock;
                  const product = orderable ? found : undefined;
                  const affordable = available !== null && available >= r.points && !!product;
                  const short = available !== null ? r.points - available : null;
                  // Every card stays full colour — the grid is the shop
                  // window. "locked" now only means: tapping explains how
                  // many points are missing (toast) instead of redeeming.
                  const state = !signedIn ? "idle" : affordable ? "on" : "locked";
                  const openRedeem = state === "on" && product && canRedeem ? () => setRedeeming({ reward: r, product }) : undefined;
                  const explainShortfall =
                    state === "locked" && product && short !== null && short > 0
                      ? () =>
                          ToastService.showToast(
                            `Not enough points yet — ${short.toLocaleString()} more for ${product.productName}`,
                            { variant: "warning" }
                          )
                      : undefined;
                  return (
                    <div key={r.id} className={`rw-card ${state}`} style={openRedeem ? { cursor: "pointer" } : undefined} onClick={openRedeem || explainShortfall}>
                      <div className="rw-figure">
                        {/* Display comes from `found` (the menu item even
                            when unavailable) — selling out must not strip
                            the photo; only redeemability gates on `product`. */}
                        {found?.productThumbnailUrl ? (
                          <img src={found.productThumbnailUrl} alt="" />
                        ) : (
                          <HiGift className="rw-gift" />
                        )}
                      </div>
                      <div className="rw-body">
                        <div className="rw-name">{found?.productName || r.name}</div>
                        <div className="rw-meta">
                          <span className="rw-pill">
                            <HiStar /> {r.points.toLocaleString()} pts
                          </span>
                          {signedIn && !affordable && product && short !== null && short > 0 && (
                            <span className="rw-more">{short.toLocaleString()} more pts</span>
                          )}
                          {signedIn && !product && (
                            <span className="rw-note">{found ? "Currently unavailable" : "Not at this store"}</span>
                          )}
                        </div>
                        {signedIn && affordable && product && canRedeem && (
                          <button
                            className="rw-btn rw-redeem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRedeeming({ reward: r, product });
                            }}
                          >
                            <HiShoppingCart className="rw-redeem-icon" /> Redeem
                          </button>
                        )}
                        {signedIn && affordable && product && !canRedeem && (
                          <span className="rw-note">Redeemable when online ordering opens</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {redeeming && (
        <RedeemModal
          reward={redeeming.reward}
          productName={redeeming.product.productName || redeeming.reward.name}
          image={redeeming.product.productThumbnailUrl}
          available={available ?? 0}
          email={auth.user?.email || ""}
          redemption={redemption}
          canOnline={canOnline}
          onAddToCart={() => {
            addReward(redeeming.reward, redeeming.product);
            setRedeeming(null);
          }}
          onScan={() => {
            setScanFor(redeeming.product.productName || redeeming.reward.name);
            setRedeeming(null);
          }}
          onClose={() => setRedeeming(null)}
          vars={vars}
          textColor={theme.textColor}
          cardColor={theme.backgroundColor}
          borderColor={theme.borderColor}
        />
      )}
      {scanFor !== null && <QrScanner onResult={onScanned} onClose={() => setScanFor(null)} />}
    </div>
  );
};

export default RewardsPage;
